import FormPDF, { IFormInput, IFormPDFRef } from '@components/pdf/form-pdf';
import { TableRow } from '@components/pdf/react-pdf/react-pdf.interface';
import useAppContext from '@hooks/useAppContext';
// import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useToast from '@hooks/useToast';
import useUser from '@hooks/useUser';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useLazyGetGuestFolioQuery } from '../../../../../api/front-office/reports.api.rtk';
import {
  dateFormat,
  formatNumber,
  toReportInputs,
} from '../../../../../utils/format';
import { FolioTotal } from '../../../interface/reports.interface';

export const GuestFolioModal = ({
  handleModalClose,
  bookingReferenceId,
  isOpen,
  subGroupValue,
  selectedSubGroup,
}: {
  handleModalClose?: () => void;
  bookingReferenceId?: string;
  isOpen: boolean;
  subGroupValue?: string;
  selectedSubGroup?: number;
}) => {
  const userData = useUser();
  const { startLoading, stopLoading } = useAppContext();
  const formRef = useRef<IFormPDFRef>(null);
  const { toast } = useToast();

  // const [openDialog, setOpenDialog] = useState(false);

  const [getGuestFolio] = useLazyGetGuestFolioQuery();

  const handleGetGuestFolio = async (bookingReferenceId: string) => {
    try {
      const filterParams = new URLSearchParams();
      filterParams.append('bookingReferenceId', bookingReferenceId);

      if (subGroupValue && subGroupValue !== 'All') {
        const filterValue: string =
          subGroupValue === 'Charges' || subGroupValue === 'Payments'
            ? subGroupValue
            : String(selectedSubGroup);
        if (filterValue) {
          filterParams.append('filter[subGroup]', filterValue);
        }
      }

      const result = await getGuestFolio(
        `?${filterParams.toString()}`,
      ).unwrap();

      if (!result) return { folio: null, totals: null };

      const totals = result?.data?.folio[0].ledgerDetails?.reduce(
        (acc, transaction) => {
          acc.totalCharges += transaction?.charges || 0;
          acc.totalPayments += transaction?.collection || 0;
          acc.totalBalance = acc.totalCharges - acc.totalPayments;
          return acc;
        },
        {
          totalCharges: 0,
          totalPayments: 0,
          totalBalance: 0,
        } as FolioTotal,
      );
      return { folio: result?.data?.folio?.[0], totals };
    } catch (error) {
      toast({ message: error, severity: 'warning' });
      return { folio: null, totals: null };
    }
  };

  const handlePrint = async (hasSalesBreakdown: boolean) => {
    try {
      // setOpenDialog(false);
      startLoading();

      if (!formRef.current) {
        toast({ severity: 'warning', message: 'No PDF Component Found' });
        return;
      }

      const { getHeaderDetails, getPropertyLogo, handleShowPDF, getTemplate } =
        formRef.current;

      const { folio, totals } = await handleGetGuestFolio(bookingReferenceId!);

      const template = await getTemplate({
        department: 'FO',
        category: 'Reservations',
        templateName: folio?.isCityLedger ? 'Guest Folio CL' : 'Guest Folio',
      });

      if (!template) return;

      const { propertyLogo } = await getPropertyLogo();
      const headerDetails = getHeaderDetails({ moduleName: 'Xenia FOS' });

      const ledgerDetails: TableRow[] = [
        ...[...(folio?.ledgerDetails ?? [])]
          .sort(
            (a, b) =>
              new Date(a.postingDate).getTime() -
              new Date(b.postingDate).getTime(),
          )
          .map(
            (item): TableRow => ({
              cells: [
                {
                  value: item.postingDate?.toString()
                    ? moment(item.postingDate).format('DD MMM YY')
                    : '',
                  id: 'postingDate',
                },
                { value: item.item?.toString() ?? '', id: 'item' },
                {
                  value: item.lineRemarks?.toString() ?? '',
                  id: 'lineRemarks',
                },
                {
                  value: item.entryRemarks?.toString() ?? '',
                  id: 'entryRemarks',
                },
                {
                  value: item.discountRemarks?.toString() ?? '',
                  id: 'discountRemarks',
                },
                {
                  value: `${formatNumber(item.charges, 2, 2)}`,
                  id: 'charges',
                },
                {
                  value: `${formatNumber(item.collection, 2, 2)}`,
                  id: 'collection',
                },
                {
                  value: `${formatNumber(item.adjustments, 2, 2)}`,
                  id: 'adjustments',
                },
              ],
            }),
          ),
      ];

      const footerLedgerDetails: TableRow[] = [
        {
          cells: [
            {
              value: formatNumber(totals?.totalPayments ?? '', 2, 2) ?? '-',
              id: 'totalPayments',
            },
          ],
        },
        {
          cells: [
            {
              value: formatNumber(totals?.totalBalance ?? '', 2, 2) ?? '-',
              id: 'totalBalance',
            },
          ],
        },
      ];

      const footer: TableRow[] = [
        {
          cells: [
            {
              value: folio?.reservedBy
                ? `${folio.reservedBy?.toString() ?? ''}\n${moment(
                    folio?.reservedByDT,
                  ).format('lll')}`
                : '',
              id: 'reservedBy',
            },
            {
              value: folio?.checkedInBy
                ? `${folio.checkedInBy?.toString() ?? ''}\n${moment(
                    folio?.checkedInDT,
                  ).format('lll')}`
                : '',
              id: 'checkedInBy',
            },
            {
              value: folio?.checkedOutBy
                ? `${folio.checkedOutBy?.toString() ?? ''}\n${moment(
                    folio?.checkedOutDT,
                  ).format('lll')}`
                : '',
              id: 'checkedOutBy',
            },
            {
              value: userData?.name?.toString() || '',
              id: 'preparedBy',
            },
          ],
        },
      ];

      const inputs: IFormInput = {
        ...(propertyLogo && { propertyLogo }),
        ...headerDetails,
        ...toReportInputs(folio ?? {}),
        ledgerDetails,
        footerLedgerDetails,
        amountVATable: formatNumber(folio?.amountVATable ?? '', 2, 2),
        amountVAT: formatNumber(folio?.amountVAT ?? '', 2, 2),
        amountVATExempt: formatNumber(folio?.amountVATExempt ?? '', 2, 2),
        amountZeroRated: formatNumber(folio?.amountZeroRated ?? '', 2, 2),
        amountServiceCharge: formatNumber(
          folio?.amountServiceCharge ?? '',
          2,
          2,
        ),
        footer,
        arrival: moment(folio?.arrival).format(dateFormat),
        departure: moment(folio?.departure).format(dateFormat),
        totalPayments: formatNumber(totals?.totalPayments ?? 0, 2, 2),
        totalBalance: formatNumber(totals?.totalBalance ?? 0, 2, 2),
        totalCharges: formatNumber(totals?.totalCharges ?? 0, 2, 2),

        salesBreakdown: String(hasSalesBreakdown),
        clNetAmount: formatNumber(folio?.clNetAmount ?? 0, 2, 2),
        clRemarks: folio?.clRemarks ?? '',
      };

      handleShowPDF({
        template,
        inputs,
      });
    } catch (error) {
      handleModalClose?.();
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (!isOpen || !bookingReferenceId) return;
    // setOpenDialog(true);
    (async () => await handlePrint(true))();
  }, [isOpen, bookingReferenceId]);

  return (
    <>
      <FormPDF
        {...{
          ref: formRef,
          title: 'Guest Folio',
          modalOnClose: handleModalClose,
        }}
      />

      {/* <ConfirmationDialog
        open={openDialog}
        title='Include Sales Breakdown'
        desc='Do you want to include sales breakdown?'
        onConfirm={async () => await handlePrint(true)}
        cancelButtonTitle='No'
        confirmButtonTitle='Yes'
        onClose={async () => await handlePrint(false)}
      /> */}
    </>
  );
};
